import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO, addMonths, addDays, differenceInCalendarDays, isValid } from "date-fns";
import { it } from "date-fns/locale";

// Components
import { GanttChart } from "./components/GanttChart";
import { ResourceGrid } from "./components/ResourceGrid";
import { HolidayCalendar } from "./components/HolidayCalendar";
import { ProjectDialog } from "./components/ProjectDialog";
import { TaskDialog } from "./components/TaskDialog";
import { ResourceDialog } from "./components/ResourceDialog";
import { LoginPage } from "./components/LoginPage";
import { UsersTab } from "./components/UsersTab";
import { JournalTab } from "./components/JournalTab";
import { MacroProjectDialog } from "./components/MacroProjectDialog";
import { ImportDialog } from "./components/ImportDialog";
import { Dashboard } from "./components/Dashboard";
import { ProjectAnalysisTab } from "./components/ProjectAnalysisTab";
import { AiAgentBubble } from "./components/AiAgentBubble";
import { AiInsightView, exportAiInsightToPptx } from "./components/AiInsightView";

// UI Components
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Calendar } from "./components/ui/calendar";
import { Checkbox } from "./components/ui/checkbox";
import { ScrollArea } from "./components/ui/scroll-area";

// Icons
import { Sun, Moon, Plus, Download, Upload, Calendar as CalendarIcon, Users, ChevronDown, ChevronLeft, LayoutGrid, LayoutList, UserPlus, Loader2, Settings2, LogOut, User, Shield, FolderKanban, Layers, Sparkles, Umbrella, ClipboardList, Archive, Bell, BarChart3, Presentation } from "lucide-react";

// Utils
import { exportGanttToExcel } from "./utils/exportUtils";
import { DEFAULT_START_DATE, DEFAULT_END_DATE, DATE_RANGE_PRESETS } from "./utils/dateUtils";
import { cn } from "./lib/utils";
import { STANDARD_TEMPLATE_TASKS } from "./data/projectTemplates";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

const PERM_MACRO_PROJECT_CREATE = "macro_project:create";
const PERM_PROJECT_CREATE = "project:create";
const PERM_TASK_CREATE = "task:create";
const PERM_WORKLOAD_VIEW = "workload:view";
const PERM_PROJECT_ANALYSIS_VIEW = "project_analysis:view";
const PERM_DASHBOARD_VIEW = "dashboard:view";
const PERM_AI_INSIGHT = "ai_insight:use";
const PERM_IMPORT = "import:use";
const PERM_EXPORT = "export:use";
const PERM_JOURNAL_VIEW = "journal:view";
const PERM_AI_AGENT_MUTATE = "ai_agent:mutate";

const AI_GEMINI_MODELS = [
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
  { value: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash Lite' }
];

const LEGACY_EXPORT_PERMS = new Set([
  PERM_MACRO_PROJECT_CREATE,
  PERM_PROJECT_CREATE,
  PERM_TASK_CREATE,
  PERM_WORKLOAD_VIEW,
  PERM_PROJECT_ANALYSIS_VIEW,
  PERM_DASHBOARD_VIEW,
  PERM_AI_INSIGHT,
  PERM_IMPORT,
  PERM_EXPORT,
  PERM_JOURNAL_VIEW,
  PERM_AI_AGENT_MUTATE
]);

const normalizePermissions = function(rawPermissions) {
  if (rawPermissions === 'admin') {
    return { role: 'admin', perms: new Set(LEGACY_EXPORT_PERMS) };
  }
  if (rawPermissions === 'view' || !rawPermissions) {
    return { role: 'view', perms: new Set() };
  }
  if (rawPermissions === 'export') {
    return { role: 'custom', perms: new Set(LEGACY_EXPORT_PERMS) };
  }
  if (Array.isArray(rawPermissions)) {
    return { role: 'custom', perms: new Set(rawPermissions) };
  }
  return { role: 'view', perms: new Set() };
};

// Create axios instance with auth
const createAuthAxios = function(token) {
  const instance = axios.create();
  instance.interceptors.request.use(function(config) {
    if (token) {
      config.headers.Authorization = 'Bearer ' + token;
    }
    return config;
  });
  return instance;
};

const buildTemplateTasks = function(startDate, endDate, taskNames, projectId, baseSortOrder) {
  if (!Array.isArray(taskNames) || taskNames.length === 0) return [];
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  if (!isValid(start) || !isValid(end)) return [];
  const normalizedStart = start <= end ? start : end;
  const normalizedEnd = start <= end ? end : start;
  const spanDays = differenceInCalendarDays(normalizedEnd, normalizedStart) + 1;
  const totalDays = spanDays > 0 ? spanDays : 1;
  const count = taskNames.length;

  return taskNames.map(function(name, index) {
    const startOffset = Math.floor((totalDays * index) / count);
    const endOffset = Math.floor((totalDays * (index + 1)) / count) - 1;
    const safeEndOffset = Math.max(startOffset, endOffset);
    return {
      name: name,
      projectId: projectId,
      startDate: format(addDays(normalizedStart, startOffset), 'yyyy-MM-dd'),
      endDate: format(addDays(normalizedStart, safeEndOffset), 'yyyy-MM-dd'),
      progress: 0,
      resourceAllocations: [],
      sortOrder: baseSortOrder + index
    };
  });
};

const GanttApp = ({ token, permissions, onLogout }) => {
  const { theme, toggleTheme } = useTheme();
  const authAxios = useMemo(function() { return createAuthAxios(token); }, [token]);
  
  // Permission checks
  const normalizedPermissions = useMemo(function() {
    return normalizePermissions(permissions);
  }, [permissions]);
  const isAdmin = normalizedPermissions.role === 'admin';
  const isViewOnly = normalizedPermissions.role === 'view';
  const hasPermission = useCallback(function(permission) {
    if (isAdmin) return true;
    if (normalizedPermissions.role === 'view') return false;
    return normalizedPermissions.perms.has(permission);
  }, [isAdmin, normalizedPermissions]);
  const canEditMacroProjects = isAdmin || hasPermission(PERM_MACRO_PROJECT_CREATE);
  const canEditProjects = isAdmin || hasPermission(PERM_PROJECT_CREATE);
  const canEditTasks = isAdmin || hasPermission(PERM_TASK_CREATE);
  const canEditResources = canEditTasks;
  const canImport = isAdmin || hasPermission(PERM_IMPORT);
  const canExport = isAdmin || hasPermission(PERM_EXPORT);
  const canViewWorkloads = isAdmin || isViewOnly || hasPermission(PERM_WORKLOAD_VIEW);
  const canViewProjectAnalysis = isAdmin || isViewOnly || hasPermission(PERM_PROJECT_ANALYSIS_VIEW);
  const canViewDashboard = isAdmin || isViewOnly || hasPermission(PERM_DASHBOARD_VIEW);
  const canViewAiInsight = isAdmin || isViewOnly || hasPermission(PERM_AI_INSIGHT);
  const canViewJournal = isAdmin || hasPermission(PERM_JOURNAL_VIEW);
  const canManageJournal = isAdmin;
  const canGenerateAiInsight = isAdmin || hasPermission(PERM_AI_INSIGHT);
  const canUseAiAgent = isAdmin || hasPermission(PERM_AI_AGENT_MUTATE);
  const canEditAny = canEditMacroProjects || canEditProjects || canEditTasks || canEditResources;
  
  // State
  const [macroProjects, setMacroProjects] = useState([]);
  const [selectedMacroProject, setSelectedMacroProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [resources, setResources] = useState([]);
  const [viewMode, setViewMode] = useState('week');
  const [projectDefaultView, setProjectDefaultView] = useState('expand');
  const [projectCollapseSignal, setProjectCollapseSignal] = useState(0);
  const [projectExpandSignal, setProjectExpandSignal] = useState(0);
  const [ganttResourceFilter, setGanttResourceFilter] = useState('all');
  const [taskNameFilter, setTaskNameFilter] = useState([]);
  const [taskFilterQuery, setTaskFilterQuery] = useState('');
  const [activeTab, setActiveTab] = useState('gantt');
  const [loading, setLoading] = useState(true);
  
  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: DEFAULT_START_DATE,
    endDate: DEFAULT_END_DATE
  });
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  
  // Dialog states
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [macroProjectDialogOpen, setMacroProjectDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDialogProjectId, setTaskDialogProjectId] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedMacroProjectForEdit, setSelectedMacroProjectForEdit] = useState(null);

  // Holidays state: { [resourceId]: { [dateString]: true } }
  const [holidays, setHolidays] = useState({});

  // AI Insight state
  const [aiInsight, setAiInsight] = useState({ text: '', createdAt: null, model: null });
  const [aiInsightLoading, setAiInsightLoading] = useState(false);
  const [aiInsightError, setAiInsightError] = useState('');
  const [aiProvider, setAiProvider] = useState('gemini');
  const [aiModel, setAiModel] = useState('gemini-2.5-flash-lite');
  const [mentionUsers, setMentionUsers] = useState([]);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const unreadCountRef = useRef(0);

  const username = localStorage.getItem('gantt-username') || 'admin';

  // Filter projects by selected macro project
  const filteredProjects = useMemo(function() {
    if (!selectedMacroProject) {
      return projects;
    }
    return projects.filter(function(p) { return p.macroProjectId === selectedMacroProject; });
  }, [projects, selectedMacroProject]);

  // Get project IDs for filtering tasks
  const filteredProjectIds = useMemo(function() {
    return new Set(filteredProjects.map(function(p) { return p.id; }));
  }, [filteredProjects]);

  // Filter tasks by filtered projects
  const filteredTasks = useMemo(function() {
    if (!selectedMacroProject) {
      return tasks;
    }
    return tasks.filter(function(t) { return filteredProjectIds.has(t.projectId); });
  }, [tasks, selectedMacroProject, filteredProjectIds]);

  // Deduplicate data from DB
  const uniqueProjects = useMemo(function() {
    const seen = {};
    const result = [];
    for (let i = 0; i < filteredProjects.length; i++) {
      if (!seen[filteredProjects[i].id]) {
        seen[filteredProjects[i].id] = true;
        result.push(filteredProjects[i]);
      }
    }
    return result;
  }, [filteredProjects]);

  const uniqueTasks = useMemo(function() {
    const seen = {};
    const result = [];
    for (let i = 0; i < filteredTasks.length; i++) {
      if (!seen[filteredTasks[i].id]) {
        seen[filteredTasks[i].id] = true;
        result.push(filteredTasks[i]);
      }
    }
    return result;
  }, [filteredTasks]);

  const taskNameOptions = useMemo(function() {
    const namesSet = new Set();
    for (let i = 0; i < uniqueTasks.length; i++) {
      const name = (uniqueTasks[i].name || '').trim();
      if (name) namesSet.add(name);
    }
    const names = Array.from(namesSet);
    names.sort(function(a, b) { return a.localeCompare(b, 'it', { sensitivity: 'base' }); });
    return names;
  }, [uniqueTasks]);

  const taskNameFilterSet = useMemo(function() {
    return new Set(taskNameFilter);
  }, [taskNameFilter]);

  const filteredTaskNameOptions = useMemo(function() {
    const query = taskFilterQuery.trim().toLowerCase();
    if (!query) return taskNameOptions;
    return taskNameOptions.filter(function(name) {
      return name.toLowerCase().includes(query);
    });
  }, [taskNameOptions, taskFilterQuery]);

  const uniqueResources = useMemo(function() {
    const seen = {};
    const result = [];
    for (let i = 0; i < resources.length; i++) {
      if (!seen[resources[i].id]) {
        seen[resources[i].id] = true;
        result.push(resources[i]);
      }
    }
    return result;
  }, [resources]);

  const tasksById = useMemo(function() {
    const map = {};
    for (let i = 0; i < tasks.length; i++) {
      if (tasks[i] && tasks[i].id) {
        map[tasks[i].id] = tasks[i];
      }
    }
    return map;
  }, [tasks]);

  const projectsById = useMemo(function() {
    const map = {};
    for (let i = 0; i < projects.length; i++) {
      if (projects[i] && projects[i].id) {
        map[projects[i].id] = projects[i];
      }
    }
    return map;
  }, [projects]);

  const ganttTasks = useMemo(function() {
    let filtered = uniqueTasks;
    if (ganttResourceFilter !== 'all') {
      filtered = filtered.filter(function(task) {
        const allocations = task.resourceAllocations || [];
        for (let i = 0; i < allocations.length; i++) {
          if (allocations[i].resourceId === ganttResourceFilter) {
            return true;
          }
        }
        return false;
      });
    }
    if (taskNameFilterSet.size > 0) {
      filtered = filtered.filter(function(task) {
        return taskNameFilterSet.has(task.name);
      });
    }
    return filtered;
  }, [uniqueTasks, ganttResourceFilter, taskNameFilterSet]);

  const projectStats = useMemo(function() {
    const progressTotals = {};
    const taskCounts = {};
    for (let i = 0; i < uniqueTasks.length; i++) {
      const task = uniqueTasks[i];
      const pid = task.projectId;
      if (!pid) continue;
      let progress = task.progress;
      if (typeof progress !== 'number') {
        const parsed = parseInt(progress, 10);
        progress = Number.isNaN(parsed) ? 0 : parsed;
      }
      progressTotals[pid] = (progressTotals[pid] || 0) + progress;
      taskCounts[pid] = (taskCounts[pid] || 0) + 1;
    }
    const avgProgress = {};
    Object.keys(taskCounts).forEach(function(pid) {
      avgProgress[pid] = Math.round(progressTotals[pid] / taskCounts[pid]);
    });
    return { avgProgress, taskCounts };
  }, [uniqueTasks]);

  const completedProjectIds = useMemo(function() {
    const set = new Set();
    for (let i = 0; i < uniqueProjects.length; i++) {
      const pid = uniqueProjects[i].id;
      const count = projectStats.taskCounts[pid] || 0;
      if (count > 0 && (projectStats.avgProgress[pid] || 0) >= 100) {
        set.add(pid);
      }
    }
    return set;
  }, [uniqueProjects, projectStats]);

  const activeProjects = useMemo(function() {
    return uniqueProjects.filter(function(p) { return !completedProjectIds.has(p.id); });
  }, [uniqueProjects, completedProjectIds]);

  const archivedProjects = useMemo(function() {
    return uniqueProjects.filter(function(p) { return completedProjectIds.has(p.id); });
  }, [uniqueProjects, completedProjectIds]);

  const activeProjectIds = useMemo(function() {
    return new Set(activeProjects.map(function(p) { return p.id; }));
  }, [activeProjects]);

  const archivedProjectIds = useMemo(function() {
    return new Set(archivedProjects.map(function(p) { return p.id; }));
  }, [archivedProjects]);

  const ganttTasksActive = useMemo(function() {
    return ganttTasks.filter(function(t) { return activeProjectIds.has(t.projectId); });
  }, [ganttTasks, activeProjectIds]);

  const ganttTasksArchived = useMemo(function() {
    return ganttTasks.filter(function(t) { return archivedProjectIds.has(t.projectId); });
  }, [ganttTasks, archivedProjectIds]);

  const ganttProjectsActive = useMemo(function() {
    if (ganttResourceFilter === 'all') {
      return activeProjects;
    }
    const projectIds = new Set(ganttTasksActive.map(function(t) { return t.projectId; }));
    return activeProjects.filter(function(p) { return projectIds.has(p.id); });
  }, [activeProjects, ganttTasksActive, ganttResourceFilter]);

  const ganttProjectsArchived = useMemo(function() {
    if (ganttResourceFilter === 'all') {
      return archivedProjects;
    }
    const projectIds = new Set(ganttTasksArchived.map(function(t) { return t.projectId; }));
    return archivedProjects.filter(function(p) { return projectIds.has(p.id); });
  }, [archivedProjects, ganttTasksArchived, ganttResourceFilter]);

  // Load data from API
  const loadData = useCallback(async function(options) {
    const silent = options && options.silent === true;
    try {
      if (!silent) setLoading(true);
      const responses = await Promise.all([
        authAxios.get(API + "/macro-projects"),
        authAxios.get(API + "/projects"),
        authAxios.get(API + "/tasks"),
        authAxios.get(API + "/resources"),
        authAxios.get(API + "/holidays")
      ]);

      setMacroProjects(responses[0].data);
      setProjects(responses[1].data);
      setTasks(responses[2].data);
      setResources(responses[3].data);
      setHolidays(responses[4].data || {});
      try {
        const mentionsResponse = await authAxios.get(API + "/users/mentions");
        const list = Array.isArray(mentionsResponse.data?.users) ? mentionsResponse.data.users : [];
        setMentionUsers(list);
      } catch (e) {
        setMentionUsers([]);
      }

      // If no projects, seed data (only on the initial non-silent load)
      if (!silent && responses[1].data.length === 0) {
        await authAxios.post(API + "/seed");
        const seedResponses = await Promise.all([
          authAxios.get(API + "/macro-projects"),
          authAxios.get(API + "/projects"),
          authAxios.get(API + "/tasks"),
          authAxios.get(API + "/resources")
        ]);
        setMacroProjects(seedResponses[0].data);
        setProjects(seedResponses[1].data);
        setTasks(seedResponses[2].data);
        setResources(seedResponses[3].data);
        try {
          const mentionsResponse = await authAxios.get(API + "/users/mentions");
          const list = Array.isArray(mentionsResponse.data?.users) ? mentionsResponse.data.users : [];
          setMentionUsers(list);
        } catch (e) {
          setMentionUsers([]);
        }
        // Auto-select first macro project
        if (seedResponses[0].data.length > 0) {
          setSelectedMacroProject(seedResponses[0].data[0].id);
        }
      } else if (responses[0].data.length > 0 && !selectedMacroProject) {
        // Auto-select first macro project on initial load
        setSelectedMacroProject(responses[0].data[0].id);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      if (error.response && error.response.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente il login.");
        onLogout();
      } else if (!silent) {
        toast.error("Errore nel caricamento dei dati");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authAxios, onLogout, selectedMacroProject]);

  const handleAgentActionExecuted = useCallback(function() {
    loadData({ silent: true });
  }, [loadData]);

  useEffect(function() {
    loadData();
  }, []);

  const buildNotificationText = useCallback(function(notification) {
    const actor = notification?.actor || 'Qualcuno';
    const taskName = notification?.taskName || (notification?.taskId ? tasksById[notification.taskId]?.name : '');
    const projectName = notification?.projectName || (notification?.projectId ? projectsById[notification.projectId]?.name : '');
    let text = actor + ' ti ha taggato';
    if (taskName) {
      text += ' nel task ' + taskName;
    }
    if (projectName) {
      text += ' del progetto ' + projectName;
    }
    return text;
  }, [projectsById, tasksById]);

  const loadNotifications = useCallback(async function(options) {
    const opts = options || {};
    const silent = !!opts.silent;
    const showToast = !!opts.showToast;
    if (!silent) {
      setNotificationsLoading(true);
    }
    try {
      const response = await authAxios.get(API + "/notifications");
      const data = response.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      const count = typeof data.unreadCount === 'number'
        ? data.unreadCount
        : items.filter(function(n) { return !n.readAt; }).length;
      setNotifications(items);
      setUnreadCount(count);
      if (showToast && count > unreadCountRef.current) {
        const diff = count - unreadCountRef.current;
        toast.info(diff === 1 ? 'Hai 1 nuova notifica' : ('Hai ' + diff + ' nuove notifiche'));
      }
      unreadCountRef.current = count;
    } catch (error) {
      if (!silent) {
        console.error("Error loading notifications:", error);
      }
    } finally {
      if (!silent) {
        setNotificationsLoading(false);
      }
    }
  }, [authAxios]);

  const markNotificationsRead = useCallback(async function() {
    if (unreadCountRef.current === 0) return;
    try {
      await authAxios.post(API + "/notifications/mark-read");
      const nowIso = new Date().toISOString();
      setNotifications(function(prev) {
        return (prev || []).map(function(n) {
          if (!n.readAt) {
            return { ...n, readAt: nowIso };
          }
          return n;
        });
      });
      unreadCountRef.current = 0;
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking notifications read:", error);
    }
  }, [authAxios]);

  const handleNotificationsOpenChange = useCallback(function(open) {
    setNotificationsOpen(open);
    if (open) {
      void (async function() {
        await loadNotifications({ silent: false, showToast: false });
        await markNotificationsRead();
      })();
    }
  }, [loadNotifications, markNotificationsRead]);

  const handleNotificationClick = useCallback(function(notification) {
    if (!notification || !notification.taskId) return;
    const task = tasksById[notification.taskId];
    if (!task) {
      toast.error('Task non trovato');
      return;
    }
    const project = task.projectId ? projectsById[task.projectId] : null;
    if (project && project.macroProjectId) {
      if (project.macroProjectId !== selectedMacroProject) {
        setSelectedMacroProject(project.macroProjectId);
      }
    } else if (selectedMacroProject) {
      setSelectedMacroProject(null);
    }
    setSelectedTask(task);
    setTaskDialogProjectId(null);
    setActiveTab('gantt');
    setTaskDialogOpen(true);
    setNotificationsOpen(false);
  }, [projectsById, selectedMacroProject, tasksById]);

  const handleInsightTaskClick = useCallback(function(taskId) {
    const task = tasksById[taskId];
    if (!task) {
      toast.error('Task non trovato');
      return;
    }
    const project = task.projectId ? projectsById[task.projectId] : null;
    if (project && project.macroProjectId && project.macroProjectId !== selectedMacroProject) {
      setSelectedMacroProject(project.macroProjectId);
    }
    setSelectedTask(task);
    setTaskDialogProjectId(null);
    setActiveTab('gantt');
    setTaskDialogOpen(true);
  }, [tasksById, projectsById, selectedMacroProject]);

  const handleInsightProjectClick = useCallback(function(projectId) {
    const project = projectsById[projectId];
    if (!project) {
      toast.error('Progetto non trovato');
      return;
    }
    if (project.macroProjectId && project.macroProjectId !== selectedMacroProject) {
      setSelectedMacroProject(project.macroProjectId);
    }
    setActiveTab('gantt');
  }, [projectsById, selectedMacroProject]);

  const handleInsightResourceClick = useCallback(function(resourceId) {
    setGanttResourceFilter(resourceId);
    setActiveTab('gantt');
  }, []);

  useEffect(function() {
    loadNotifications({ silent: true, showToast: false });
  }, [loadNotifications]);

  useEffect(function() {
    const intervalId = setInterval(function() {
      loadNotifications({ silent: true, showToast: true });
    }, 30000);
    return function() { clearInterval(intervalId); };
  }, [loadNotifications]);

  // AI Insight handlers
  const loadAiInsight = useCallback(async function() {
    try {
      const response = await authAxios.get(API + "/ai-insight");
      const data = response.data || { text: '', createdAt: null, model: null };
      setAiInsight(data);
      if (data.provider) {
        setAiProvider(data.provider);
      }
    } catch (error) {
      console.error("Error loading AI insight:", error);
    }
  }, [authAxios]);

  const handleGenerateAiInsight = useCallback(async function() {
    try {
      setAiInsightLoading(true);
      setAiInsightError('');
      const response = await authAxios.post(API + "/ai-insight", { provider: aiProvider, model: aiModel });
      setAiInsight(response.data || { text: '', createdAt: null, model: null });
      toast.success('Analisi AI aggiornata');
    } catch (error) {
      console.error("Error generating AI insight:", error);
      const message = error.response?.data?.detail || "Errore nella generazione dell'analisi AI";
      setAiInsightError(message);
      toast.error(message);
    } finally {
      setAiInsightLoading(false);
    }
  }, [authAxios, aiProvider, aiModel]);

  useEffect(function() {
    if (activeTab === 'ai-insight') {
      loadAiInsight();
    }
  }, [activeTab, loadAiInsight]);

  // Date range handlers
  const handlePresetSelect = function(months) {
    const start = parseISO(DEFAULT_START_DATE);
    const end = addMonths(start, months);
    setDateRange({
      startDate: DEFAULT_START_DATE,
      endDate: format(end, 'yyyy-MM-dd')
    });
    setDateRangeOpen(false);
  };

  const handleStartDateChange = function(date) {
    if (date) {
      setDateRange(function(prev) {
        return { ...prev, startDate: format(date, 'yyyy-MM-dd') };
      });
    }
  };

  const handleEndDateChange = function(date) {
    if (date) {
      setDateRange(function(prev) {
        return { ...prev, endDate: format(date, 'yyyy-MM-dd') };
      });
    }
  };

  // Macro Project Handlers
  const handleSaveMacroProject = useCallback(async function(macroProject) {
    if (!canEditMacroProjects) {
      toast.error('Non hai i permessi per modificare i macro progetti');
      return;
    }
    try {
      const isEditing = selectedMacroProjectForEdit !== null;
      if (isEditing) {
        await authAxios.put(API + "/macro-projects/" + macroProject.id, {
          name: macroProject.name,
          description: macroProject.description,
          color: macroProject.color
        });
        setMacroProjects(function(prev) {
          return prev.map(function(mp) { return mp.id === macroProject.id ? macroProject : mp; });
        });
        toast.success('Macro progetto aggiornato');
      } else {
        const response = await authAxios.post(API + "/macro-projects", {
          name: macroProject.name,
          description: macroProject.description,
          color: macroProject.color
        });
        setMacroProjects(function(prev) { return [...prev, response.data]; });
        setSelectedMacroProject(response.data.id);
        toast.success('Macro progetto creato');
      }
    } catch (error) {
      console.error("Error saving macro project:", error);
      toast.error("Errore nel salvataggio del macro progetto");
    }
  }, [selectedMacroProjectForEdit, authAxios, canEditMacroProjects]);

  const handleDeleteMacroProject = useCallback(async function(macroProjectId) {
    if (!isAdmin) {
      toast.error('Solo gli admin possono eliminare i macro progetti');
      return;
    }
    try {
      await authAxios.delete(API + "/macro-projects/" + macroProjectId);
      setMacroProjects(function(prev) {
        return prev.filter(function(mp) { return mp.id !== macroProjectId; });
      });
      // Also remove projects and tasks locally
      const projectsToRemove = projects.filter(function(p) { return p.macroProjectId === macroProjectId; });
      const projectIds = new Set(projectsToRemove.map(function(p) { return p.id; }));
      setProjects(function(prev) {
        return prev.filter(function(p) { return p.macroProjectId !== macroProjectId; });
      });
      setTasks(function(prev) {
        return prev.filter(function(t) { return !projectIds.has(t.projectId); });
      });
      // Select another macro project if the deleted one was selected
      if (selectedMacroProject === macroProjectId) {
        const remaining = macroProjects.filter(function(mp) { return mp.id !== macroProjectId; });
        setSelectedMacroProject(remaining.length > 0 ? remaining[0].id : null);
      }
      toast.success('Macro progetto eliminato');
    } catch (error) {
      console.error("Error deleting macro project:", error);
      toast.error("Errore nell'eliminazione del macro progetto");
    }
  }, [authAxios, isAdmin, projects, macroProjects, selectedMacroProject]);

  // Handlers
  const handleSaveProject = useCallback(async function(project) {
    if (!canEditProjects) {
      toast.error('Non hai i permessi per modificare i progetti');
      return;
    }
    try {
      const isEditing = selectedProject !== null;
      const fallbackTemplateTasks = project?.templateType === 'standard' ? STANDARD_TEMPLATE_TASKS : [];
      const templateTasks = Array.isArray(project?.templateTasks) && project.templateTasks.length > 0
        ? project.templateTasks
        : fallbackTemplateTasks;

      const projectData = {
        name: project.name,
        startDate: project.startDate,
        endDate: project.endDate,
        color: project.color,
        macroProjectId: project.macroProjectId || selectedMacroProject,
        saleAmount: project.saleAmount,
        estimatedRealDays: project.estimatedRealDays
      };
      
      if (isEditing) {
        const response = await authAxios.put(API + "/projects/" + project.id, projectData);
        const updatedProject = response.data;
        setProjects(function(prev) {
          return prev.map(function(p) { return p.id === updatedProject.id ? updatedProject : p; });
        });
        toast.success('Progetto aggiornato');
      } else {
        const response = await authAxios.post(API + "/projects", projectData);
        const createdProject = response.data;
        setProjects(function(prev) { return [...prev, createdProject]; });
        toast.success('Progetto creato');

        if (templateTasks.length > 0) {
          if (!canEditTasks) {
            toast.error('Non hai i permessi per creare i task del template');
          } else {
            const baseSortOrder = tasks.filter(function(t) { return t.projectId === createdProject.id; }).length;
            const templatePayloads = buildTemplateTasks(
              createdProject.startDate,
              createdProject.endDate,
              templateTasks,
              createdProject.id,
              baseSortOrder
            );
            if (templatePayloads.length > 0) {
              try {
                const responses = await Promise.all(
                  templatePayloads.map(function(taskData) {
                    return authAxios.post(API + "/tasks", taskData);
                  })
                );
                const createdTasks = responses.map(function(res) { return res.data; });
                setTasks(function(prev) { return [...prev, ...createdTasks]; });
                toast.success('Task da template creati');
              } catch (taskError) {
                console.error("Error creating template tasks:", taskError);
                toast.error("Errore nella creazione dei task da template");
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error("Errore nel salvataggio del progetto");
    }
  }, [selectedProject, authAxios, canEditProjects, selectedMacroProject, canEditTasks, tasks]);

  const handleDeleteProject = useCallback(async function(projectId) {
    if (!isAdmin) {
      toast.error('Solo gli admin possono eliminare i progetti');
      return;
    }
    try {
      await authAxios.delete(API + "/projects/" + projectId);
      setProjects(function(prev) {
        return prev.filter(function(p) { return p.id !== projectId; });
      });
      setTasks(function(prev) {
        return prev.filter(function(t) { return t.projectId !== projectId; });
      });
      toast.success('Progetto eliminato');
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Errore nell'eliminazione del progetto");
    }
  }, [authAxios, isAdmin]);

  const handleSaveTask = useCallback(async function(task) {
    if (!canEditTasks) {
      toast.error('Non hai i permessi per modificare i task');
      return;
    }
    try {
      const isEditing = selectedTask !== null;
      const taskData = {
        name: task.name,
        projectId: task.projectId,
        startDate: task.startDate,
        endDate: task.endDate,
        progress: task.progress,
        resourceAllocations: task.resourceAllocations,
        comments: task.comments || [],
        sortOrder: task.sortOrder
      };
      
      if (isEditing) {
        const response = await authAxios.put(API + "/tasks/" + task.id, taskData);
        const updatedTask = response.data;
        setTasks(function(prev) {
          return prev.map(function(t) { return t.id === updatedTask.id ? updatedTask : t; });
        });
        setSelectedTask(updatedTask);
        toast.success('Task aggiornato');
      } else {
        if (taskData.sortOrder === undefined || taskData.sortOrder === null) {
          const sameProject = tasks.filter(function(t) { return t.projectId === task.projectId; });
          let maxOrder = -1;
          for (let i = 0; i < sameProject.length; i++) {
            const so = sameProject[i].sortOrder;
            if (typeof so === 'number' && so > maxOrder) maxOrder = so;
          }
          taskData.sortOrder = maxOrder >= 0 ? maxOrder + 1 : sameProject.length;
        }
        const response = await authAxios.post(API + "/tasks", taskData);
        setTasks(function(prev) { return [...prev, response.data]; });
        toast.success('Task creato');
      }
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Errore nel salvataggio del task");
    }
  }, [selectedTask, authAxios, canEditTasks]);

  const handleDeleteTask = useCallback(async function(taskId) {
    if (!canEditTasks) {
      toast.error('Non hai i permessi per eliminare i task');
      return;
    }
    try {
      await authAxios.delete(API + "/tasks/" + taskId);
      setTasks(function(prev) {
        return prev.filter(function(t) { return t.id !== taskId; });
      });
      toast.success('Task eliminato');
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Errore nell'eliminazione del task");
    }
  }, [authAxios, canEditTasks]);

  const handleSaveResource = useCallback(async function(resource) {
    if (!canEditResources) {
      toast.error('Non hai i permessi per modificare le risorse');
      return;
    }
    try {
      const isEditing = selectedResource !== null;
      const resourceData = {
        name: resource.name,
        avatar: resource.avatar,
        csr: resource.csr
      };
      if (isEditing) {
        const response = await authAxios.put(API + "/resources/" + resource.id, resourceData);
        const updatedResource = response.data;
        setResources(function(prev) {
          return prev.map(function(r) { return r.id === updatedResource.id ? updatedResource : r; });
        });
        toast.success('Risorsa aggiornata');
      } else {
        const response = await authAxios.post(API + "/resources", resourceData);
        setResources(function(prev) { return [...prev, response.data]; });
        toast.success('Risorsa creata');
      }
    } catch (error) {
      console.error("Error saving resource:", error);
      toast.error("Errore nel salvataggio della risorsa");
    }
  }, [selectedResource, authAxios, canEditResources]);

  const handleDeleteResource = useCallback(async function(resourceId) {
    if (!canEditResources) {
      toast.error('Non hai i permessi per eliminare le risorse');
      return;
    }
    try {
      await authAxios.delete(API + "/resources/" + resourceId);
      setResources(function(prev) {
        return prev.filter(function(r) { return r.id !== resourceId; });
      });
      toast.success('Risorsa eliminata');
    } catch (error) {
      console.error("Error deleting resource:", error);
      toast.error("Errore nell'eliminazione della risorsa");
    }
  }, [authAxios, canEditResources]);

  // Handler for drag & drop task update
  const handleTaskDragUpdate = useCallback(async function(updatedTask) {
    if (!canEditTasks) return;
    
    try {
      await authAxios.put(API + "/tasks/" + updatedTask.id, {
        name: updatedTask.name,
        projectId: updatedTask.projectId,
        startDate: updatedTask.startDate,
        endDate: updatedTask.endDate,
        progress: updatedTask.progress,
        resourceAllocations: updatedTask.resourceAllocations,
        comments: updatedTask.comments || [],
        sortOrder: updatedTask.sortOrder
      });
      
      setTasks(function(prev) {
        return prev.map(function(t) { return t.id === updatedTask.id ? updatedTask : t; });
      });
    } catch (error) {
      console.error("Error updating task position:", error);
      toast.error("Errore nell'aggiornamento del task");
    }
  }, [authAxios, canEditTasks]);

  const handleTaskReorder = useCallback(async function(updatedTasks) {
    if (!canEditTasks) return;
    if (!updatedTasks || updatedTasks.length === 0) return;
    const updatesById = {};
    for (let i = 0; i < updatedTasks.length; i++) {
      updatesById[updatedTasks[i].id] = updatedTasks[i];
    }
    setTasks(function(prev) {
      return prev.map(function(t) { return updatesById[t.id] ? updatesById[t.id] : t; });
    });
    try {
      await Promise.all(updatedTasks.map(function(t) {
        return authAxios.put(API + "/tasks/" + t.id, {
          name: t.name,
          projectId: t.projectId,
          startDate: t.startDate,
          endDate: t.endDate,
          progress: t.progress,
          resourceAllocations: t.resourceAllocations,
          comments: t.comments || [],
          sortOrder: t.sortOrder
        });
      }));
    } catch (error) {
      console.error("Error reordering tasks:", error);
      toast.error("Errore nel riordino dei task");
      loadData();
    }
  }, [authAxios, canEditTasks, loadData]);

  // Handler for importing data from Excel with macro project selection
  const handleImport = useCallback(async function(file, macroProjectId) {
    if (!canImport) {
      toast.error('Non hai i permessi per importare dati');
      return;
    }
    
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      let url = API + "/import";
      if (macroProjectId) {
        url += "?macro_project_id=" + macroProjectId;
      }
      
      const response = await authAxios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Import completato: ' + response.data.imported.projects + ' progetti, ' + response.data.imported.tasks + ' task, ' + response.data.imported.resources + ' risorse');
      
      // Reload data
      await loadData();
    } catch (error) {
      console.error("Error importing data:", error);
      toast.error("Errore nell'importazione: " + (error.response?.data?.detail || error.message));
    }
  }, [authAxios, canImport, loadData]);

  // Handler for importing holidays from Hera CSV
  const handleImportHolidays = useCallback(async function(file) {
    if (!canImport) {
      toast.error('Non hai i permessi per importare dati');
      return;
    }
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).map(function(line) { return line.split(';'); });

      // Find key row indices
      let monthRowIdx = -1;
      let dayRowIdx = -1;
      let personRowIdx = -1;
      for (let i = 0; i < lines.length; i++) {
        const first = (lines[i][0] || '').trim();
        if (first === 'Mese Anno') monthRowIdx = i;
        else if (first === 'Giorno') dayRowIdx = i;
        else if (first === 'Persona') personRowIdx = i;
      }
      if (monthRowIdx < 0 || dayRowIdx < 0 || personRowIdx < 0) {
        toast.error('Formato CSV non riconosciuto. Verificare le intestazioni Mese Anno / Giorno / Persona');
        return;
      }

      const italianMonths = {
        'Gennaio': 1, 'Febbraio': 2, 'Marzo': 3, 'Aprile': 4,
        'Maggio': 5, 'Giugno': 6, 'Luglio': 7, 'Agosto': 8,
        'Settembre': 9, 'Ottobre': 10, 'Novembre': 11, 'Dicembre': 12
      };
      const italianMonthsAbbr = {
        'gen': 1, 'feb': 2, 'mar': 3, 'apr': 4,
        'mag': 5, 'giu': 6, 'lug': 7, 'ago': 8,
        'set': 9, 'ott': 10, 'nov': 11, 'dic': 12
      };

      const monthRow = lines[monthRowIdx];
      const dayRow = lines[dayRowIdx];

      // Build column -> date string mapping
      const columnToDate = {};
      let currentMonth = null;
      for (let col = 0; col < Math.max(monthRow.length, dayRow.length); col++) {
        const monthVal = (monthRow[col] || '').trim();
        if (monthVal && monthVal !== 'Mese Anno') {
          let parsed = false;
          // Handle "Aprile 2026" format
          const parts = monthVal.split(' ');
          if (parts.length >= 2) {
            const monthNum = italianMonths[parts[0]];
            const year = parseInt(parts[1]);
            if (monthNum && year) {
              currentMonth = { month: monthNum, year: year };
              parsed = true;
            }
          }
          // Handle "apr-26" abbreviated format
          if (!parsed) {
            const abbrParts = monthVal.toLowerCase().split('-');
            if (abbrParts.length === 2) {
              const monthNum = italianMonthsAbbr[abbrParts[0]];
              const yearSuffix = parseInt(abbrParts[1]);
              if (monthNum && !isNaN(yearSuffix)) {
                const year = yearSuffix < 100 ? 2000 + yearSuffix : yearSuffix;
                currentMonth = { month: monthNum, year: year };
              }
            }
          }
        }
        const dayVal = (dayRow[col] || '').trim();
        const dayNum = parseInt(dayVal);
        if (currentMonth && dayVal && !isNaN(dayNum) && dayVal !== 'NC' && dayVal !== 'R') {
          const dateStr = currentMonth.year + '-' +
            currentMonth.month.toString().padStart(2, '0') + '-' +
            dayNum.toString().padStart(2, '0');
          columnToDate[col] = dateStr;
        }
      }

      // Build resource name (lowercase) -> id map
      const resourceNameMap = {};
      for (let i = 0; i < resources.length; i++) {
        resourceNameMap[resources[i].name.trim().toLowerCase()] = resources[i].id;
      }

      const colEntries = Object.keys(columnToDate).map(function(k) {
        return { col: parseInt(k), date: columnToDate[k] };
      });

      if (colEntries.length === 0) {
        toast.error('Nessuna data trovata nel CSV. Verifica il formato delle colonne (es. "apr-26" o "Aprile 2026").');
        return;
      }

      const newHolidays = {};
      const skippedNames = [];
      let totalDays = 0;

      for (let rowIdx = personRowIdx + 1; rowIdx < lines.length; rowIdx++) {
        const row = lines[rowIdx];
        if (!row || !row[0] || !row[0].trim()) continue;
        const personName = row[0].trim();
        const resourceId = resourceNameMap[personName.toLowerCase()];
        if (!resourceId) {
          if (skippedNames.indexOf(personName) < 0) skippedNames.push(personName);
          continue;
        }
        if (!newHolidays[resourceId]) newHolidays[resourceId] = {};
        for (let ci = 0; ci < colEntries.length; ci++) {
          const { col, date } = colEntries[ci];
          const val = (row[col] || '').trim();
          const hours = parseFloat(val.replace(',', '.'));
          if (!isNaN(hours) && hours > 0) {
            newHolidays[resourceId][date] = hours;
            totalDays++;
          }
        }
      }

      const merged = Object.assign({}, holidays);
      const rids = Object.keys(newHolidays);
      for (let i = 0; i < rids.length; i++) {
        merged[rids[i]] = newHolidays[rids[i]];
      }
      setHolidays(merged);

      await authAxios.post(API + "/holidays", { data: merged });

      const loadedResources = Object.keys(newHolidays).length;
      toast.success('Piano ferie importato: ' + loadedResources + ' risorse, ' + totalDays + ' giorni di ferie');
      if (skippedNames.length > 0) {
        console.log('Risorse ignorate (non trovate a sistema):', skippedNames.join(', '));
        toast.info('Risorse ignorate (non trovate): ' + skippedNames.join(', '));
      }
    } catch (error) {
      console.error("Error importing holidays:", error);
      toast.error("Errore nell'importazione del piano ferie: " + error.message);
    }
  }, [canImport, resources, holidays, authAxios]);

  const handleUpdateHoliday = useCallback(async function(resourceId, dateStr, hours) {
    if (!canImport) return;
    setHolidays(function(prev) {
      const updated = { ...prev, [resourceId]: { ...prev[resourceId] } };
      if (hours > 0) {
        updated[resourceId][dateStr] = hours;
      } else {
        delete updated[resourceId][dateStr];
      }
      authAxios.post(API + "/holidays", { data: updated }).catch(function(e) {
        console.error("Error saving holiday:", e);
        toast.error("Errore nel salvataggio delle ferie");
      });
      return updated;
    });
  }, [canImport, authAxios]);

  const handleProjectClick = useCallback(function(project) {
    if (canEditProjects) {
      setSelectedProject(project);
      setProjectDialogOpen(true);
    }
  }, [canEditProjects]);

  const handleTaskClick = useCallback(function(task) {
    if (canEditTasks) {
      setSelectedTask(task);
      setTaskDialogProjectId(null);
      setTaskDialogOpen(true);
    }
  }, [canEditTasks]);

  const handleResourceClick = useCallback(function(resource) {
    if (canEditResources) {
      setSelectedResource(resource);
      setResourceDialogOpen(true);
    }
  }, [canEditResources]);

  const handleNewProject = useCallback(function() {
    setSelectedProject(null);
    setProjectDialogOpen(true);
  }, []);

  const handleNewTask = useCallback(function() {
    setSelectedTask(null);
    setTaskDialogProjectId(null);
    setTaskDialogOpen(true);
  }, []);

  const handleNewTaskForProject = useCallback(function(project) {
    if (!canEditTasks) return;
    setSelectedTask(null);
    setTaskDialogProjectId(project?.id || null);
    setTaskDialogOpen(true);
  }, [canEditTasks]);

  const handleNewResource = useCallback(function() {
    setSelectedResource(null);
    setResourceDialogOpen(true);
  }, []);

  const handleNewMacroProject = useCallback(function() {
    setSelectedMacroProjectForEdit(null);
    setMacroProjectDialogOpen(true);
  }, []);

  const handleEditMacroProject = useCallback(function() {
    const mp = macroProjects.find(function(m) { return m.id === selectedMacroProject; });
    if (mp) {
      setSelectedMacroProjectForEdit(mp);
      setMacroProjectDialogOpen(true);
    }
  }, [macroProjects, selectedMacroProject]);

  // Export only the selected macro project's data
  const handleExport = useCallback(function() {
    if (!canExport) {
      toast.error('Non hai i permessi per esportare');
      return;
    }
    const mpName = macroProjects.find(function(m) { return m.id === selectedMacroProject; })?.name || 'Tutti';
    exportGanttToExcel(uniqueProjects, uniqueTasks, uniqueResources, viewMode, dateRange.startDate, dateRange.endDate, mpName, holidays);
    toast.success('Export completato: ' + mpName);
  }, [uniqueProjects, uniqueTasks, uniqueResources, viewMode, dateRange, canExport, selectedMacroProject, macroProjects, holidays]);

  const handleLogout = function() {
    localStorage.removeItem('gantt-token');
    localStorage.removeItem('gantt-token-expires');
    localStorage.removeItem('gantt-username');
    localStorage.removeItem('gantt-permissions');
    onLogout();
  };

  // Get current macro project name
  const currentMacroProjectName = useMemo(function() {
    if (!selectedMacroProject) return 'Tutti i Progetti';
    const mp = macroProjects.find(function(m) { return m.id === selectedMacroProject; });
    return mp ? mp.name : 'Tutti i Progetti';
  }, [selectedMacroProject, macroProjects]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Caricamento dati...</p>
        </div>
      </div>
    );
  }

  // Determine number of tabs for grid
  const tabCount = isAdmin ? 3 : 2;

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo & Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                <Layers className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight" data-testid="app-title">
                  InstantGantt
                </h1>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  Gestione progetti e risorse
                </p>
              </div>
            </div>

            {/* Macro Project Selector */}
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 max-w-[200px]" data-testid="macro-project-selector">
                    <FolderKanban className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="truncate">{currentMacroProjectName}</span>
                    <ChevronDown className="w-3 h-3 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-56">
                  <DropdownMenuItem 
                    onClick={function() { setSelectedMacroProject(null); }}
                    className={!selectedMacroProject ? 'bg-accent' : ''}
                  >
                    <Layers className="w-4 h-4 mr-2" />
                    Tutti i Progetti
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {macroProjects.map(function(mp) {
                    return (
                      <DropdownMenuItem 
                        key={mp.id}
                        onClick={function() { setSelectedMacroProject(mp.id); }}
                        className={selectedMacroProject === mp.id ? 'bg-accent' : ''}
                      >
                        <div 
                          className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                          style={{ backgroundColor: mp.color }}
                        />
                        {mp.name}
                      </DropdownMenuItem>
                    );
                  })}
                  {canEditMacroProjects && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleNewMacroProject}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nuovo Macro Progetto
                      </DropdownMenuItem>
                      {selectedMacroProject && (
                        <DropdownMenuItem onClick={handleEditMacroProject}>
                          <Settings2 className="w-4 h-4 mr-2" />
                          Modifica "{currentMacroProjectName}"
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Date Range Selector */}
              <Popover open={dateRangeOpen} onOpenChange={setDateRangeOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5 hidden md:flex" data-testid="date-range-btn">
                    <Settings2 className="w-4 h-4" />
                    <span className="text-xs">
                      {format(parseISO(dateRange.startDate), 'MMM yy', { locale: it })} - {format(parseISO(dateRange.endDate), 'MMM yy', { locale: it })}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="end">
                  <div className="space-y-4">
                    <div className="font-medium text-sm">Periodo Visualizzato</div>
                    
                    <div className="flex flex-wrap gap-2">
                      {DATE_RANGE_PRESETS.map(function(preset, idx) {
                        return (
                          <Button 
                            key={'preset-' + idx}
                            variant="outline" 
                            size="sm"
                            onClick={function() { handlePresetSelect(preset.months); }}
                          >
                            {preset.label}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <div className="border-t border-border pt-4">
                      <div className="text-xs text-muted-foreground mb-2">Personalizzato</div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="text-xs font-medium">Da</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="w-full justify-start text-xs">
                                <CalendarIcon className="w-3 h-3 mr-1" />
                                {format(parseISO(dateRange.startDate), 'dd/MM/yy')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={parseISO(dateRange.startDate)}
                                onSelect={handleStartDateChange}
                                locale={it}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium">A</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="w-full justify-start text-xs">
                                <CalendarIcon className="w-3 h-3 mr-1" />
                                {format(parseISO(dateRange.endDate), 'dd/MM/yy')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={parseISO(dateRange.endDate)}
                                onSelect={handleEndDateChange}
                                locale={it}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* View Mode Toggle */}
              {(activeTab === 'gantt') && (
                <div className="hidden sm:flex items-center gap-2">
                  <div className="flex items-center bg-muted rounded-lg p-1">
                    <Button
                      variant={viewMode === 'day' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={function() { setViewMode('day'); }}
                      className="h-8 px-3 text-xs"
                      data-testid="view-mode-day"
                    >
                      <LayoutList className="w-4 h-4 mr-1.5" />
                      Giorno
                    </Button>
                    <Button
                      variant={viewMode === 'week' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={function() { setViewMode('week'); }}
                      className="h-8 px-3 text-xs"
                      data-testid="view-mode-week"
                    >
                      <LayoutGrid className="w-4 h-4 mr-1.5" />
                      Settimana
                    </Button>
                  </div>

                  <div className="flex items-center bg-muted rounded-lg p-1">
                    <Button
                      variant={projectDefaultView === 'expand' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={function() {
                        setProjectDefaultView('expand');
                        setProjectExpandSignal(function(prev) { return prev + 1; });
                      }}
                      className="h-8 px-3 text-xs"
                      data-testid="expand-projects"
                    >
                      <ChevronDown className="w-4 h-4 mr-1.5" />
                      Expand
                    </Button>
                    <Button
                      variant={projectDefaultView === 'collapse' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={function() {
                        setProjectDefaultView('collapse');
                        setProjectCollapseSignal(function(prev) { return prev + 1; });
                      }}
                      className="h-8 px-3 text-xs"
                      data-testid="collapse-projects"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1.5" />
                      Collapse
                    </Button>
                  </div>
                </div>
              )}

              {/* Add Dropdown - only if can edit */}
              {canEditAny && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" className="gap-1.5" data-testid="add-menu-trigger">
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">Aggiungi</span>
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEditProjects && (
                      <DropdownMenuItem onClick={handleNewProject} data-testid="add-project-btn">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        Nuovo Progetto
                      </DropdownMenuItem>
                    )}
                    {canEditTasks && (
                      <DropdownMenuItem onClick={handleNewTask} data-testid="add-task-btn">
                        <LayoutList className="w-4 h-4 mr-2" />
                        Nuovo Task
                      </DropdownMenuItem>
                    )}
                    {canEditResources && (
                      <DropdownMenuItem onClick={handleNewResource} data-testid="add-resource-btn">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Nuova Risorsa
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Export Button - only if can export */}
              {canExport && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleExport}
                  className="gap-1.5"
                  data-testid="export-btn"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Esporta</span>
                </Button>
              )}

              {/* Import Button - only if can import */}
              {canImport && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={function() { setImportDialogOpen(true); }}
                  className="gap-1.5"
                  data-testid="import-btn"
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Importa</span>
                </Button>
              )}

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9"
                data-testid="theme-toggle"
              >
                {theme === 'light' ? (
                  <Moon className="w-4 h-4" />
                ) : (
                  <Sun className="w-4 h-4" />
                )}
              </Button>

              {/* Notifications */}
              <Popover open={notificationsOpen} onOpenChange={handleNotificationsOpenChange}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative h-9 w-9" data-testid="notifications-trigger">
                    <Bell className={cn("w-4 h-4", unreadCount > 0 ? "text-amber-500" : "")} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] leading-[18px] text-center">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-96 p-0">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                    <span className="text-sm font-medium">Notifiche</span>
                    {unreadCount > 0 && (
                      <span className="text-xs text-muted-foreground">Nuove: {unreadCount}</span>
                    )}
                  </div>
                  <ScrollArea className="max-h-80">
                    {notificationsLoading && (
                      <div className="px-3 py-4 text-sm text-muted-foreground">Caricamento...</div>
                    )}
                    {!notificationsLoading && notifications.length === 0 && (
                      <div className="px-3 py-4 text-sm text-muted-foreground">Nessuna notifica disponibile.</div>
                    )}
                    {!notificationsLoading && notifications.map(function(notification) {
                      const dateValue = notification?.createdAt ? parseISO(notification.createdAt) : null;
                      const dateLabel = dateValue && isValid(dateValue)
                        ? format(dateValue, 'dd/MM/yyyy HH:mm')
                        : 'Data non disponibile';
                      const isUnread = !notification.readAt;
                      return (
                        <div
                          key={notification.id}
                          className={cn(
                            "px-3 py-3 border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40",
                            isUnread ? "bg-amber-50/60" : "bg-background"
                          )}
                          onClick={function() { handleNotificationClick(notification); }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={function(e) {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleNotificationClick(notification);
                            }
                          }}
                        >
                          <div className="text-sm">{buildNotificationText(notification)}</div>
                          {notification.commentText && (
                            <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
                              {notification.commentText}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mt-1">{dateLabel}</div>
                        </div>
                      );
                    })}
                  </ScrollArea>
                </PopoverContent>
              </Popover>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{username}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5 text-sm">
                    <div className="font-medium">{username}</div>
                    <div className="text-xs text-muted-foreground">
                      {normalizedPermissions.role === 'admin' && 'Amministratore'}
                      {normalizedPermissions.role === 'view' && 'Solo Visualizzazione'}
                      {normalizedPermissions.role === 'custom' && 'Permessi personalizzati'}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Full Height */}
      <main className="flex-1 flex flex-col overflow-hidden px-4 lg:px-6 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <TabsList className="w-auto flex-nowrap overflow-x-auto">
                <TabsTrigger value="gantt" className="gap-2" data-testid="tab-gantt">
                  <CalendarIcon className="w-4 h-4" />
                  Gantt
                </TabsTrigger>
                <TabsTrigger value="archive" className="gap-2 min-w-0" data-testid="tab-archive">
                  <Archive className="w-4 h-4 shrink-0" />
                  <span className="truncate">Storico</span>
                </TabsTrigger>
                {canViewWorkloads && (
                  <TabsTrigger value="resources" className="gap-2 min-w-0" data-testid="tab-resources">
                    <Users className="w-4 h-4 shrink-0" />
                    <span className="truncate">Carichi</span>
                  </TabsTrigger>
                )}
                {canViewWorkloads && (
                  <TabsTrigger value="ferie" className="gap-2 min-w-0" data-testid="tab-ferie">
                    <Umbrella className="w-4 h-4 shrink-0" />
                    <span className="truncate">Piano Ferie</span>
                  </TabsTrigger>
                )}
                {canViewProjectAnalysis && (
                  <TabsTrigger value="project-analysis" className="gap-2 min-w-0" data-testid="tab-project-analysis">
                    <BarChart3 className="w-4 h-4 shrink-0" />
                    <span className="truncate">Analisi Progetti</span>
                  </TabsTrigger>
                )}
                {canViewDashboard && (
                  <TabsTrigger value="dashboard" className="gap-2" data-testid="tab-dashboard">
                    <LayoutGrid className="w-4 h-4" />
                    Dashboard
                  </TabsTrigger>
                )}
                {canViewAiInsight && (
                  <TabsTrigger value="ai-insight" className="gap-2" data-testid="tab-ai-insight">
                    <Sparkles className="w-4 h-4" />
                    AI Insight
                  </TabsTrigger>
                )}
                {isAdmin && (
                  <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
                    <Shield className="w-4 h-4" />
                    Utenti
                  </TabsTrigger>
                )}
                {canViewJournal && (
                  <TabsTrigger value="journal" className="gap-2" data-testid="tab-journal">
                    <ClipboardList className="w-4 h-4" />
                    Journal
                  </TabsTrigger>
                )}
              </TabsList>

              {(activeTab === 'gantt' || activeTab === 'archive') && (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-2 bg-muted rounded-lg px-2 py-1">
                    <span className="text-xs text-muted-foreground hidden sm:inline">Risorsa</span>
                    <select
                      value={ganttResourceFilter}
                      onChange={function(e) { setGanttResourceFilter(e.target.value); }}
                      className="h-8 px-2 text-xs rounded-md border border-input bg-background"
                      data-testid="gantt-resource-filter"
                    >
                      <option value="all">Tutte</option>
                      {uniqueResources.map(function(resource) {
                        return (
                          <option key={'gantt-res-' + resource.id} value={resource.id}>
                            {resource.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 px-3 text-xs" data-testid="gantt-task-filter-trigger">
                        Task: {taskNameFilter.length === 0 ? 'Tutti' : taskNameFilter.length + ' selezionati'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-2" align="start">
                      <div className="flex items-center justify-between gap-2 px-1 pb-2 border-b border-border">
                        <span className="text-xs text-muted-foreground">Filtro Task</span>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={function() { setTaskNameFilter(filteredTaskNameOptions); }}
                            disabled={filteredTaskNameOptions.length === 0}
                            data-testid="gantt-task-filter-all"
                          >
                            Tutti
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={function() { setTaskNameFilter([]); }}
                            disabled={taskNameFilter.length === 0}
                            data-testid="gantt-task-filter-clear"
                          >
                            Pulisci
                          </Button>
                        </div>
                      </div>
                      <div className="px-1 pt-2">
                        <Input
                          value={taskFilterQuery}
                          onChange={function(e) { setTaskFilterQuery(e.target.value); }}
                          placeholder="Filtra per testo..."
                          className="h-8 text-xs"
                          data-testid="gantt-task-filter-search"
                        />
                      </div>
                      <div className="max-h-56 overflow-auto py-2">
                        {taskNameOptions.length === 0 && (
                          <div className="text-xs text-muted-foreground px-1">Nessun task disponibile</div>
                        )}
                        {taskNameOptions.length > 0 && filteredTaskNameOptions.length === 0 && (
                          <div className="text-xs text-muted-foreground px-1">Nessun task trovato</div>
                        )}
                        {filteredTaskNameOptions.map(function(name, index) {
                          const id = 'task-filter-' + index;
                          const checked = taskNameFilterSet.has(name);
                          return (
                            <label key={name} htmlFor={id} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted/50 cursor-pointer">
                              <Checkbox
                                id={id}
                                checked={checked}
                                onCheckedChange={function(nextChecked) {
                                  const isChecked = !!nextChecked;
                                  setTaskNameFilter(function(prev) {
                                    if (isChecked) {
                                      if (prev.indexOf(name) !== -1) return prev;
                                      return prev.concat(name);
                                    }
                                    return prev.filter(function(n) { return n !== name; });
                                  });
                                }}
                              />
                              <span className="text-sm truncate" title={name}>{name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>

            {/* Mobile View Mode Toggle */}
            {(activeTab === 'gantt' || activeTab === 'archive') && (
              <div className="sm:hidden flex items-center gap-2">
                <div className="flex items-center bg-muted rounded-lg p-1">
                  <Button
                    variant={viewMode === 'day' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={function() { setViewMode('day'); }}
                    className="h-7 px-2 text-xs"
                  >
                    Giorno
                  </Button>
                  <Button
                    variant={viewMode === 'week' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={function() { setViewMode('week'); }}
                    className="h-7 px-2 text-xs"
                  >
                    Settimana
                  </Button>
                </div>

                <div className="flex items-center bg-muted rounded-lg p-1">
                  <Button
                    variant={projectDefaultView === 'expand' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={function() {
                      setProjectDefaultView('expand');
                      setProjectExpandSignal(function(prev) { return prev + 1; });
                    }}
                    className="h-7 px-2 text-xs"
                  >
                    <ChevronDown className="w-3 h-3 mr-1" />
                    Expand
                  </Button>
                  <Button
                    variant={projectDefaultView === 'collapse' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={function() {
                      setProjectDefaultView('collapse');
                      setProjectCollapseSignal(function(prev) { return prev + 1; });
                    }}
                    className="h-7 px-2 text-xs"
                  >
                    <ChevronLeft className="w-3 h-3 mr-1" />
                    Collapse
                  </Button>
                </div>
              </div>
            )}
          </div>

          <TabsContent value="gantt" className="flex-1 overflow-hidden mt-0">
            <GanttChart
              projects={ganttProjectsActive}
              tasks={ganttTasksActive}
              resources={uniqueResources}
              viewMode={viewMode}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              defaultExpandMode={projectDefaultView}
              collapseSignal={projectCollapseSignal}
              expandSignal={projectExpandSignal}
              onProjectClick={handleProjectClick}
              onTaskClick={handleTaskClick}
              onTaskUpdate={canEditTasks ? handleTaskDragUpdate : undefined}
              onTaskReorder={canEditTasks ? handleTaskReorder : undefined}
              onProjectAddTask={canEditTasks ? handleNewTaskForProject : undefined}
            />
          </TabsContent>

          <TabsContent value="archive" className="flex-1 overflow-hidden mt-0">
            {archivedProjects.length === 0 ? (
              <div className="h-full bg-card border border-border rounded-lg p-6 flex items-center justify-center text-sm text-muted-foreground">
                Nessun progetto archiviato.
              </div>
            ) : (
              <GanttChart
                projects={ganttProjectsArchived}
                tasks={ganttTasksArchived}
                resources={uniqueResources}
                viewMode={viewMode}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                defaultExpandMode={projectDefaultView}
                collapseSignal={projectCollapseSignal}
                expandSignal={projectExpandSignal}
                onProjectClick={handleProjectClick}
                onTaskClick={handleTaskClick}
                onTaskUpdate={canEditTasks ? handleTaskDragUpdate : undefined}
                onTaskReorder={canEditTasks ? handleTaskReorder : undefined}
                onProjectAddTask={canEditTasks ? handleNewTaskForProject : undefined}
              />
            )}
          </TabsContent>

          {canViewWorkloads && (
            <TabsContent value="resources" className="flex-1 overflow-hidden mt-0">
              <ResourceGrid
                resources={uniqueResources}
                tasks={uniqueTasks}
                projects={uniqueProjects}
                holidays={holidays}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onResourceClick={canEditResources ? handleResourceClick : undefined}
              />
            </TabsContent>
          )}

          {canViewWorkloads && (
            <TabsContent value="ferie" className="flex-1 overflow-hidden mt-0">
              <HolidayCalendar
                resources={uniqueResources}
                holidays={holidays}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onUpdateHoliday={handleUpdateHoliday}
                canEdit={canImport}
              />
            </TabsContent>
          )}

          {canViewProjectAnalysis && (
            <TabsContent value="project-analysis" className="flex-1 overflow-hidden mt-0">
              <ProjectAnalysisTab
                projects={uniqueProjects}
                tasks={uniqueTasks}
                resources={uniqueResources}
              />
            </TabsContent>
          )}

          {canViewDashboard && (
            <TabsContent value="dashboard" className="flex-1 overflow-auto mt-0">
              <Dashboard
                projects={uniqueProjects}
                tasks={uniqueTasks}
                resources={uniqueResources}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
              />
            </TabsContent>
          )}

          {canViewAiInsight && (
            <TabsContent value="ai-insight" className="flex-1 overflow-auto mt-0">
              <div className="h-full bg-card border border-border rounded-lg p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      Analisi AI
                    </h2>
                    {aiInsight.createdAt && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Ultimo aggiornamento: {new Date(aiInsight.createdAt).toLocaleString('it-IT')}
                        {aiInsight.model && <span className="ml-2 opacity-60">({aiInsight.model})</span>}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={function() { exportAiInsightToPptx(aiInsight); }}
                      variant="outline"
                      className="gap-2"
                      disabled={!aiInsight || !aiInsight.sections}
                      data-testid="export-ai-insight-pptx-btn"
                    >
                      <Presentation className="w-4 h-4" />
                      Esporta PPTX
                    </Button>
                    <select
                      value={aiModel}
                      onChange={function(e) { setAiModel(e.target.value); }}
                      className="h-9 px-2 text-xs rounded-md border border-input bg-background"
                      data-testid="ai-model-select"
                      disabled={!canGenerateAiInsight || aiInsightLoading}
                    >
                      {AI_GEMINI_MODELS.map(function(m) {
                        return <option key={m.value} value={m.value}>{m.label}</option>;
                      })}
                    </select>
                    <Button
                      onClick={handleGenerateAiInsight}
                      disabled={aiInsightLoading || !canGenerateAiInsight}
                      data-testid="ai-insight-generate"
                    >
                      {aiInsightLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analisi in corso...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Analisi AI
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {aiInsightError && (
                  <div className="mb-4 p-3 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-destructive">
                    {aiInsightError}
                  </div>
                )}
                <div className="h-[calc(100%-6rem)] min-h-[300px] overflow-auto">
                  <AiInsightView
                    insight={aiInsight}
                    onTaskClick={handleInsightTaskClick}
                    onProjectClick={handleInsightProjectClick}
                    onResourceClick={handleInsightResourceClick}
                  />
                </div>
              </div>
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="users" className="flex-1 overflow-hidden mt-0">
              <div className="h-full bg-card border border-border rounded-lg p-6 overflow-auto">
                <UsersTab authAxios={authAxios} apiUrl={API} />
              </div>
            </TabsContent>
          )}

          {canViewJournal && (
            <TabsContent value="journal" className="flex-1 overflow-hidden mt-0">
              <div className="h-full bg-card border border-border rounded-lg p-6 overflow-auto">
                <JournalTab authAxios={authAxios} apiUrl={API} canManageJournal={canManageJournal} />
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Stats Bar */}
        <div className="flex-shrink-0 mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-card border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Macro Progetti</p>
            <p className="text-xl font-bold" data-testid="stat-macro-projects">{macroProjects.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Progetti</p>
            <p className="text-xl font-bold" data-testid="stat-projects">{uniqueProjects.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Task Totali</p>
            <p className="text-xl font-bold" data-testid="stat-tasks">{uniqueTasks.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Risorse</p>
            <p className="text-xl font-bold" data-testid="stat-resources">{uniqueResources.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Avanzamento Medio</p>
            <p className="text-xl font-bold" data-testid="stat-progress">
              {uniqueTasks.length > 0 
                ? Math.round(uniqueTasks.reduce(function(acc, t) { return acc + t.progress; }, 0) / uniqueTasks.length) 
                : 0}%
            </p>
          </div>
        </div>
      </main>

      {/* Dialogs */}
      {canEditMacroProjects && (
        <MacroProjectDialog
          open={macroProjectDialogOpen}
          onOpenChange={setMacroProjectDialogOpen}
          macroProject={selectedMacroProjectForEdit}
          onSave={handleSaveMacroProject}
          onDelete={handleDeleteMacroProject}
        />
      )}

      {canEditProjects && (
        <ProjectDialog
          open={projectDialogOpen}
          onOpenChange={setProjectDialogOpen}
          project={selectedProject}
          macroProjects={macroProjects}
          canEditTasks={canEditTasks}
          onSave={handleSaveProject}
          onDelete={isAdmin ? handleDeleteProject : undefined}
        />
      )}

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        task={selectedTask}
        defaultProjectId={taskDialogProjectId}
        projects={uniqueProjects}
        resources={uniqueResources}
        canEditTasks={canEditTasks}
        isAdmin={isAdmin}
        currentUsername={username}
        mentionUsers={mentionUsers}
        onSave={handleSaveTask}
        onDelete={canEditTasks ? handleDeleteTask : undefined}
      />

      {canEditResources && (
        <ResourceDialog
          open={resourceDialogOpen}
          onOpenChange={setResourceDialogOpen}
          resource={selectedResource}
          onSave={handleSaveResource}
          onDelete={handleDeleteResource}
        />
      )}

      {canImport && (
        <ImportDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          macroProjects={macroProjects}
          onImport={handleImport}
          onImportHolidays={handleImportHolidays}
        />
      )}

      {canUseAiAgent && (
        <AiAgentBubble
          authAxios={authAxios}
          apiBase={API}
          onActionExecuted={handleAgentActionExecuted}
        />
      )}

      <Toaster position="bottom-right" />
    </div>
  );
};

function AppWrapper() {
  const [authState, setAuthState] = useState(function() {
    const savedToken = localStorage.getItem('gantt-token');
    const expiresAt = localStorage.getItem('gantt-token-expires');
    const savedPermissionsRaw = localStorage.getItem('gantt-permissions');
    let savedPermissions = null;
    if (savedPermissionsRaw) {
      try {
        savedPermissions = JSON.parse(savedPermissionsRaw);
      } catch (e) {
        savedPermissions = savedPermissionsRaw;
      }
    }
    
    if (savedToken && expiresAt) {
      const expDate = new Date(expiresAt);
      if (expDate > new Date()) {
        return { token: savedToken, permissions: savedPermissions || 'view' };
      }
      localStorage.removeItem('gantt-token');
      localStorage.removeItem('gantt-token-expires');
      localStorage.removeItem('gantt-username');
      localStorage.removeItem('gantt-permissions');
    }
    return { token: null, permissions: null };
  });

  const handleLogin = function(token, permissions) {
    localStorage.setItem('gantt-permissions', JSON.stringify(permissions));
    setAuthState({ token: token, permissions: permissions });
  };

  const handleLogout = function() {
    setAuthState({ token: null, permissions: null });
  };

  if (!authState.token) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <GanttApp token={authState.token} permissions={authState.permissions} onLogout={handleLogout} />;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppWrapper />} />
          <Route path="*" element={<AppWrapper />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
